import React from 'react';
import { DataTable } from '../common/data-table/DataTable';
import { Column } from 'primereact/column';
import { Paginator, PaginatorPageChangeEvent } from 'primereact/paginator';
import { useTranslation } from 'react-i18next';
import SimpleBar from 'simplebar-react';
import './MatrixHeatmapTable.scss';
import useIsMobile from '../../hooks/global/UseIsMobile';
import { MOBILE_PAGINATOR_TEMPLATE } from '../../utils/mobile';

export interface HeatmapColumn {
  key: string;
  header: React.ReactNode;
  /** Kolumna wyróżniona (marka własna w macierzy Insights): bold nagłówka + ring na wartościach. */
  emphasized?: boolean;
}

export interface HeatmapRow {
  key: string;
  header: React.ReactNode;
  /** Wiersz wyróżniony (marka badana w raportach): tło całego wiersza. */
  emphasized?: boolean;
  values: Array<number | null>;
}

/** Stan paginacji trzyma wołający (przy paginacji backendowej steruje on zapytaniem). */
export interface HeatmapPagination {
  first: number;
  rows: number;
  totalRecords: number;
  rowsPerPageOptions?: number[];
  onPageChange: (event: PaginatorPageChangeEvent) => void;
}

interface MatrixHeatmapTableProps {
  /** Nagłówek pierwszej (opisowej) kolumny — "Brand" w raportach, "Prompt" na Insights. */
  rowHeader: React.ReactNode;
  columns: HeatmapColumn[];
  rows: HeatmapRow[];
  /** Nagłówki wierszy w jednej linii (raporty: nazwy marek); bez tego tekst się zawija (prompty). */
  rowHeaderNoWrap?: boolean;
  /**
   * Przy wielu kolumnach tabela jedzie w poziomie na naszym pasku (SimpleBar), a kolumna opisowa
   * zostaje przyklejona do lewej — bez niej przy przewinięciu nie wiadomo, czyj to wiersz.
   */
  scrollable?: boolean;
  /**
   * Opcjonalny paginator pod tabelą (styl jak PromptsTable) — część komponentu, jak w "Kluczowych
   * poleceniach" na overview. Raporty go nie włączają.
   */
  pagination?: HeatmapPagination;
  /**
   * Telefon: wiersze jako karty (nagłówek wiersza = tytuł, wartości w siatce po trzy z nagłówkiem
   * kolumny jako etykietą) zamiast tabeli przewijanej w bok. Raporty go nie włączają — slajdy mają
   * zachować układ tabeli.
   */
  stackOnMobile?: boolean;
}

/**
 * Nasycenie komórki. Im wyższa wartość, tym mocniejszy niebieski — od progu, na którym tło robi się
 * ciemne, napis przechodzi na biały, żeby zachować kontrast. Brak danych zostaje szary.
 */
const intensityOf = (value: number | null): string => {
  if (value === null || value === 0) return 'empty';
  if (value < 10) return '1';
  if (value < 25) return '2';
  if (value < 40) return '3';
  return '4';
};

/**
 * Generyczna heatmapa wartości % (wiersze × kolumny) na PrimeReact DataTable — bez żadnej wiedzy
 * o markach/promptach, obie osie konfigurowalne (ReactNode). Konsumenci: `ReportMatrixTable`
 * (adapter raportów "Competitor visibility" / "Brand presence by topic"; wiersze = marki)
 * i macierz "Brand visibility per prompt" na Insights (wiersze = prompty, kolumny = marki).
 */
export const MatrixHeatmapTable: React.FC<MatrixHeatmapTableProps> = ({
  rowHeader,
  columns,
  rows,
  rowHeaderNoWrap = false,
  scrollable = false,
  pagination,
  stackOnMobile = false,
}) => {
  const { t } = useTranslation();
  const isStack = useIsMobile() && stackOnMobile;
  const isScrollable = scrollable && !isStack;

  const valueBody = (row: HeatmapRow, index: number) => {
    const value = row.values[index] ?? null;
    return (
      <span
        className={`matrix-heatmap__cell matrix-heatmap__cell--${intensityOf(value)}${
          columns[index]?.emphasized ? ' matrix-heatmap__cell--col-emphasized' : ''
        }`}
      >
        {value === null ? '-' : `${value}%`}
      </span>
    );
  };

  const table = (
    <DataTable
      value={rows}
      dataKey="key"
      // `scrollable` włącza w PrimeReact obsługę `frozen` — bez niego kolumna się nie przykleja.
      scrollable={isScrollable}
      tableClassName="table"
      className={`table-wrapper matrix-heatmap${isStack ? ' table-wrapper--stack' : ''}`}
      responsiveLayout={isStack ? 'stack' : undefined}
      // Bez tego kolumny ścisnęłyby się do szerokości kontenera i nie byłoby czego przewijać.
      tableStyle={isScrollable ? { minWidth: 'max-content' } : undefined}
      rowClassName={(row: HeatmapRow) => (row.emphasized ? 'matrix-heatmap__row--emphasized' : '')}
    >
      <Column
        header={rowHeader}
        frozen={isScrollable}
        body={(row: HeatmapRow) => row.header}
        className={`matrix-heatmap__row-header${
          rowHeaderNoWrap ? ' matrix-heatmap__row-header--nowrap' : ''
        }`}
        bodyClassName="table__cell--stack-title"
        headerClassName="matrix-heatmap__row-header-cell"
      />
      {columns.map((column, index) => (
        <Column
          key={column.key}
          headerClassName={`table__header table__header--center${
            column.emphasized ? ' matrix-heatmap__col-header--emphasized' : ''
          }`}
          header={column.header}
          body={(row: HeatmapRow) => valueBody(row, index)}
          className="matrix-heatmap__value-col"
        />
      ))}
    </DataTable>
  );

  const paginator = pagination ? (
    <Paginator
      className="pagination"
      first={pagination.first}
      rows={pagination.rows}
      totalRecords={pagination.totalRecords}
      rowsPerPageOptions={pagination.rowsPerPageOptions ?? [10, 25, 50]}
      currentPageReportTemplate={t('rowsNumber') + ':'}
      template={
        isStack
          ? MOBILE_PAGINATOR_TEMPLATE
          : 'FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown'
      }
      pageLinkSize={isStack ? 3 : 5}
      onPageChange={pagination.onPageChange}
    />
  ) : null;

  if (!scrollable && !pagination) {
    return table;
  }

  if (isStack) {
    return (
      <div className="matrix-heatmap__stack">
        {table}
        {paginator}
      </div>
    );
  }

  // Ramka niesie obramowanie tabeli, bo sama tabela jest szersza od kadru i jej prawa krawędź
  // uciekłaby poza ekran — ten sam układ, co na `AdminPermissionsPage`. Paginator siedzi
  // WEWNĄTRZ ramki (wizualnie jest częścią tabeli, jak w PromptsTable / "Kluczowych poleceniach"),
  // ale pod SimpleBarem — nie może jeździć w poziomie razem z kolumnami.
  return (
    <div className="matrix-heatmap__frame">
      {scrollable ? <SimpleBar autoHide={false}>{table}</SimpleBar> : table}
      {paginator}
    </div>
  );
};

export default MatrixHeatmapTable;
